import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOMeta from "@/components/SEOMeta";
import LocalBusinessSchema from "@/components/LocalBusinessSchema";
import SchemaMarkup from "@/components/SchemaMarkup";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { 
  Wrench, 
  Shield, 
  CheckCircle,
  Phone,
  Award,
  Clock,
  MapPin,
  Home,
  Building,
  Factory,
  AlertTriangle,
  Droplets,
  Settings,
  Star,
  Users,
  Mail,
  Zap,
  Quote
} from "lucide-react";

// Import plumbing images
import plumberImage from "@/assets/plumber-ontario.jpg";
import plumberInstallation from "@/assets/plumber-installation.jpg";
import plumberRepair from "@/assets/plumber-repair.jpg";
import emergencyPlumbing from "@/assets/emergency-plumbing.jpg";
import commercialPlumbing from "@/assets/commercial-plumbing.jpg";

const PlumberServices = () => {
  const faqData = [
    {
      question: "Do you provide emergency plumbing services in Niagara Region, Hamilton, and Toronto?",
      answer: "Yes. Blue Knight Contracting provides 24/7 emergency plumbing services throughout Niagara Region (St. Catharines, Niagara Falls, Welland), Hamilton, Toronto, and the entire GTA for burst pipes, water leaks, sewer backups, and urgent plumbing repairs."
    },
    {
      question: "Are your plumbers licensed and insured in Ontario?",
      answer: "Absolutely. All our plumbers are fully licensed and insured in Ontario with extensive experience in residential, commercial, and industrial plumbing. We comply with all Ontario Building Code requirements and carry comprehensive liability insurance for your protection."
    },
    {
      question: "What types of water heaters do you install in Niagara and Hamilton?",
      answer: "We install and service all types of water heaters including traditional tank water heaters, tankless water heaters, heat pump water heaters, and high-efficiency models. We work with top brands like Rheem, Bradford White, AO Smith, and Navien for reliable hot water solutions in Niagara Region, Hamilton, and Toronto GTA."
    },
    {
      question: "Do you offer drain cleaning and camera inspection services?",
      answer: "Yes. We provide comprehensive drain cleaning using professional snaking and hydro-jetting equipment. Our camera inspection services help identify blockages, root intrusion, and pipe damage in Niagara, Hamilton, and Toronto properties. We service all drain types including kitchen sinks, bathroom drains, floor drains, and main sewer lines."
    },
    {
      question: "Can you install and repair bathroom and kitchen fixtures?",
      answer: "Yes. Our licensed plumbers install and repair all types of plumbing fixtures including toilets, sinks, faucets, bathtubs, showers, dishwashers, and garbage disposals. We work with premium brands like Kohler, Delta, Moen, and American Standard to ensure quality installations in Niagara Region, Hamilton, and Toronto homes."
    },
    {
      question: "Do you handle commercial plumbing for businesses in Hamilton and Toronto?",
      answer: "Yes. Our licensed plumbers provide complete commercial plumbing services for offices, restaurants, retail stores, industrial facilities, and multi-unit buildings throughout Hamilton, Toronto GTA, and Niagara Region. Services include grease trap installation, backflow prevention, commercial water heaters, and ongoing maintenance programs."
    },
    {
      question: "What should I do if I have a burst pipe emergency?",
      answer: "If you have a burst pipe, immediately shut off your main water valve and call our 24/7 emergency plumbing line. Our emergency plumbers serve Niagara Region, Hamilton, and Toronto with rapid response times to minimize water damage. We'll repair the pipe, assess for additional damage, and help with insurance documentation."
    },
    {
      question: "Do you offer preventive plumbing maintenance services?",
      answer: "Yes. We offer comprehensive plumbing maintenance programs for residential and commercial properties in Niagara, Hamilton, and Toronto. Services include annual inspections, water heater flushing, drain cleaning, fixture checks, and leak detection to prevent costly emergency repairs."
    }
  ];

  const serviceData = {
    "@type": "Service",
    name: "Plumbing Services Ontario",
    description: "Professional licensed plumbing services across Ontario including installation, repair, emergency services, and maintenance for residential and commercial properties.",
    provider: {
      "@type": "Organization",
      name: "Blue Knight Contracting and Construction"
    },
    serviceType: "Plumbing Services",
    areaServed: "Ontario, Canada"
  };

  const plumbingServices = [
    {
      title: "Residential Plumbing",
      description: "Complete plumbing services for homes including installation, repair, and maintenance.",
      icon: Home,
      services: [
        "Kitchen and bathroom plumbing",
        "Pipe installation and repair",
        "Fixture installation",
        "Water heater services",
        "Drain cleaning",
        "Leak detection and repair"
      ]
    },
    {
      title: "Commercial Plumbing",
      description: "Professional plumbing solutions for offices, retail, restaurants, and businesses.",
      icon: Building,
      services: [
        "Office plumbing systems",
        "Restaurant plumbing",
        "Retail space plumbing",
        "Commercial water heaters",
        "Backflow prevention",
        "Grease trap installation"
      ]
    },
    {
      title: "Industrial Plumbing",
      description: "Heavy-duty plumbing for manufacturing, warehouses, and industrial facilities.",
      icon: Factory,
      services: [
        "Industrial pipe systems",
        "Process piping",
        "Boiler connections",
        "Industrial water systems",
        "Chemical waste systems",
        "Steam and condensate lines"
      ]
    },
    {
      title: "Emergency Services",
      description: "24/7 emergency plumbing repairs for urgent water damage prevention.",
      icon: AlertTriangle,
      services: [
        "Burst pipe repairs",
        "Water leak emergency response",
        "Blocked drain clearing",
        "Sewer backup cleanup",
        "Water shut-off assistance",
        "Emergency fixture repair"
      ]
    },
    {
      title: "Water Systems",
      description: "Water heater installation, repair, and water treatment system services.",
      icon: Droplets,
      services: [
        "Water heater installation",
        "Tankless water heaters",
        "Water heater repair",
        "Water filtration systems",
        "Water softener installation",
        "Pressure tank services"
      ]
    },
    {
      title: "Drain & Sewer",
      description: "Comprehensive drain cleaning and sewer line services with camera inspection.",
      icon: Settings,
      services: [
        "Drain cleaning and snaking",
        "Sewer line repair",
        "Camera inspections",
        "Hydro jetting services",
        "Sewer line replacement",
        "Root removal services"
      ]
    }
  ];

  const customerReviews = [
    {
      name: "Michael T.",
      location: "St. Catharines, Niagara Region",
      rating: 5,
      review: "Blue Knight's plumber arrived within 2 hours for our burst pipe emergency. Professional service and fair pricing. Highly recommend for anyone in the Niagara Region!"
    },
    {
      name: "Sarah M.",
      location: "Hamilton, ON",
      rating: 5,
      review: "Excellent water heater installation in our Hamilton home. The team was punctual, clean, and explained everything. The tankless system they installed works perfectly!"
    },
    {
      name: "David K.",
      location: "Toronto, GTA",
      rating: 5,
      review: "Called for a kitchen sink drain issue in our Toronto condo. Their plumber diagnosed and fixed the problem quickly. Great communication and professional work throughout."
    },
    {
      name: "Jennifer L.",
      location: "Niagara Falls, ON",
      rating: 5,
      review: "Blue Knight installed all plumbing fixtures in our new Niagara Falls bathroom renovation. Beautiful work with attention to detail. The Kohler fixtures look amazing!"
    },
    {
      name: "Robert P.",
      location: "Mississauga, GTA",
      rating: 5,
      review: "Commercial plumbing service for our Mississauga restaurant was top-notch. They installed our grease trap and backflow preventer efficiently. Licensed and insured—exactly what we needed."
    },
    {
      name: "Lisa W.",
      location: "Welland, Niagara Region",
      rating: 5,
      review: "Had a sewer backup at our Welland property. Blue Knight's camera inspection found tree roots in the line. They cleared it same day and explained prevention tips. Very thorough!"
    }
  ];

  const serviceAreas = [
    { name: "Niagara Region", link: "/locations", featured: true },
    { name: "Hamilton", link: "/hamilton", featured: true },
    { name: "Toronto GTA", link: "/toronto", featured: true },
    { name: "St. Catharines", link: "/locations", featured: false },
    { name: "Niagara Falls", link: "/locations", featured: false },
    { name: "Welland", link: "/locations", featured: false },
    { name: "Mississauga", link: "/mississauga", featured: false },
    { name: "Burlington", link: "/locations", featured: false },
    { name: "Oakville", link: "/locations", featured: false },
    { name: "Grimsby", link: "/locations", featured: false },
    { name: "Brampton", link: "/locations", featured: false },
    { name: "Vaughan", link: "/locations", featured: false }
  ];

  return (
    <>
      <SEOMeta
        title="Plumbing Contractor — Repairs & Installations | Niagara • Hamilton • GTA"
        description="Trusted plumbers for repairs, drain cleaning and new installations serving Niagara, Hamilton & the GTA. Fast response and quality workmanship."
        keywords="plumbing contractor Niagara, Hamilton plumber, GTA plumbing service, drain cleaning, leak repair, fixture install, bathroom plumbing"
        canonicalUrl="https://blueknightcontracting.ca/services/plumbing"
      />
      
      <LocalBusinessSchema
        locationName="Niagara Region"
        addressLocality="St. Catharines"
        addressRegion="ON"
        description="Licensed plumbers serving Niagara Region, Hamilton, and Toronto GTA with professional plumbing installation, repair, emergency services, and maintenance for residential and commercial properties."
        services={[
          "Emergency Plumbing Services",
          "Licensed Plumbers",
          "Pipe Installation & Repair",
          "Fixture Installation",
          "Water Heater Services",
          "Drain Cleaning",
          "Sewer Line Repair",
          "Commercial Plumbing",
          "Bathroom Plumbing",
          "Kitchen Plumbing"
        ]}
      />
      
      <SchemaMarkup 
        type="Service"
        data={{
          ...serviceData,
          mainEntity: {
            "@type": "FAQPage",
            mainEntity: faqData.map(faq => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer
              }
            }))
          }
        }}
      />
      
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary to-primary/80 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6">
                  Licensed Plumbers Niagara Region, Hamilton & Toronto GTA
                </h1>
                <p className="text-xl mb-8 opacity-90">
                  Blue Knight Contracting provides professional licensed plumbing services across Niagara Region, Hamilton, Toronto, and the entire GTA. From 24/7 emergency repairs to new installations, water heater service, and commercial plumbing—we deliver reliable, code-compliant solutions with experienced, insured plumbers.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" variant="secondary">
                    <Link to="/contact">Get Free Quote</Link>
                  </Button>
                  <Button size="lg" variant="outline" className="text-white bg-primary border-white hover:bg-white hover:text-primary">
                    <Phone className="h-5 w-5 mr-2" />
                    24/7 Emergency Service
                  </Button>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={plumberInstallation} 
                  alt="Licensed plumber installing modern plumbing system in Ontario home"
                  className="rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Comprehensive Plumbing Services
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Our licensed plumbers provide complete plumbing solutions for residential, commercial, and industrial clients across Ontario.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {plumbingServices.map((service, index) => (
                <Card key={index} className="h-full">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle>{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.services.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Projects */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Featured Plumbing Projects
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Explore our portfolio of plumbing work across Ontario
              </p>
            </div>

            <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-8">
              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={plumberImage} 
                    alt="Ontario plumber installing residential plumbing system"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Pipe Installation</h3>
                  <p className="text-muted-foreground">
                    New plumbing system installations and upgrades
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={plumberRepair} 
                    alt="Ontario plumber repairing bathroom plumbing and fixing leaks"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Plumbing Repairs</h3>
                  <p className="text-muted-foreground">
                    Professional leak repair and maintenance services
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={commercialPlumbing} 
                    alt="Commercial plumber installing industrial plumbing systems in Ontario"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Commercial Work</h3>
                  <p className="text-muted-foreground">
                    Industrial and commercial plumbing installations
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={emergencyPlumbing} 
                    alt="Emergency plumbing service for burst pipe repair in Ontario"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Emergency Services</h3>
                  <p className="text-muted-foreground">
                    24/7 emergency repairs and water damage prevention
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Emergency Services CTA */}
        <section className="py-16 bg-destructive text-white">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-4xl mx-auto">
              <AlertTriangle className="h-16 w-16 mx-auto mb-6" />
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Plumbing Emergency? Call Now!
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Burst pipe? Water leak? Blocked drain? Don't wait - call our emergency plumbers for immediate assistance and water damage prevention.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button size="lg" variant="secondary">
                  <Phone className="h-5 w-5 mr-2" />
                  Call Emergency Line
                </Button>
                <div className="flex items-center gap-2 text-white/90">
                  <Clock className="h-5 w-5" />
                  <span>Available 24/7</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Why Choose Blue Knight Plumbing?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center p-6">
                <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Licensed & Insured</h3>
                <p className="text-muted-foreground">
                  Fully licensed Ontario plumbers with comprehensive insurance
                </p>
              </Card>

              <Card className="text-center p-6">
                <Award className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Quality Guarantee</h3>
                <p className="text-muted-foreground">
                  Warranty on all plumbing work and quality materials
                </p>
              </Card>

              <Card className="text-center p-6">
                <Clock className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">24/7 Emergency</h3>
                <p className="text-muted-foreground">
                  Round-the-clock emergency plumbing services
                </p>
              </Card>

              <Card className="text-center p-6">
                <Star className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Ontario Wide</h3>
                <p className="text-muted-foreground">
                  Serving communities across Ontario province
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Customer Reviews */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                What Our Customers Say
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Real reviews from satisfied plumbing customers in Niagara Region, Hamilton, and Toronto GTA
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {customerReviews.map((review, index) => (
                <Card key={index} className="p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-primary text-primary" />
                    ))}
                  </div>
                  <Quote className="h-8 w-8 text-primary/20 mb-2" />
                  <p className="text-muted-foreground mb-4 italic">
                    "{review.review}"
                  </p>
                  <div className="border-t pt-4">
                    <p className="font-semibold">{review.name}</p>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {review.location}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Licensed Plumbing Services Across Ontario
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Proudly serving Niagara Region, Hamilton, Toronto GTA, and surrounding areas with professional plumbing services
              </p>
            </div>

            <div className="mb-12">
              <h3 className="text-2xl font-semibold mb-6 text-center">Primary Service Areas</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {serviceAreas.filter(area => area.featured).map((area, index) => (
                  <Card key={index} className="p-6 text-center bg-primary text-white hover:shadow-lg transition-shadow">
                    <MapPin className="h-8 w-8 mx-auto mb-3" />
                    <Link to={area.link} className="font-semibold text-xl hover:underline">
                      {area.name}
                    </Link>
                    <p className="text-sm mt-2 opacity-90">Licensed Plumbing Services</p>
                  </Card>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-6 text-center">Additional Service Areas</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {serviceAreas.filter(area => !area.featured).map((area, index) => (
                  <Card key={index} className="p-4 text-center hover:shadow-md transition-shadow">
                    <MapPin className="h-5 w-5 text-primary mx-auto mb-2" />
                    <Link to={area.link} className="font-medium hover:text-primary hover:underline">
                      {area.name}
                    </Link>
                  </Card>
                ))}
              </div>
            </div>

            <div className="mt-12 text-center">
              <p className="text-muted-foreground mb-4">
                Need plumbing services in another area? <Link to="/contact" className="text-primary hover:underline font-medium">Contact us</Link> to discuss your location.
              </p>
              <p className="text-sm text-muted-foreground">
                Related Services: <Link to="/kitchen-renovations" className="text-primary hover:underline">Kitchen Renovations</Link> | <Link to="/bathroom-renovations" className="text-primary hover:underline">Bathroom Renovations</Link> | <Link to="/hvac-services" className="text-primary hover:underline">HVAC Services</Link> | <Link to="/electrician-services" className="text-primary hover:underline">Electrician Services</Link>
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Common questions about our plumbing services in Ontario
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-8">
              {faqData.map((faq, index) => (
                <Card key={index} className="p-6">
                  <h3 className="text-xl font-semibold mb-3 text-primary">
                    {faq.question}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-primary text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Need Licensed Plumbing Work?
            </h2>
            <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
              Contact Blue Knight Contracting today for professional plumbing services across Ontario. Our licensed plumbers are ready to help with all your plumbing needs.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                <span>Call: [Insert Phone Number]</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                <span>Email: [Insert Email]</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <span>Serving all of Ontario</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="secondary">
                <Link to="/contact">Get Free Plumbing Quote</Link>
              </Button>
              <Button size="lg" variant="outline" className="text-white bg-primary border-white hover:bg-white hover:text-primary">
                <Phone className="h-5 w-5 mr-2" />
                Emergency Service
              </Button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default PlumberServices;